import clsx from "clsx";
import { DateTime } from "luxon";
import React, { useContext, useEffect, useState } from "react";
import { useParams, Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Grid,
  LinearProgress,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  SvgIcon,
  Link,
} from "@material-ui/core";

import { FleetContext, FleetSettingsContext } from "../../context";

const useStyles = makeStyles((theme) => ({
  messagesWrapper: { padding: theme.spacing(1, 2, 2, 2) },
  loading: { padding: 0 },
  table: {
    marginTop: theme.spacing(2),
    background: theme.palette.background.paper,
  },
  tableHead: {
    "& *": { background: theme.palette.background.paper },
  },
  tableCell: {
    maxWidth: 200,
    borderColor: theme.palette.primary.divider,
    whiteSpace: "pre-line",
  },
  tableHeadCell: {
    padding: theme.spacing(2),
    borderColor: theme.palette.primary.divider,
    whiteSpace: "nowrap",
  },
  urlCell: {
    overflow: "hidden",
    "text-overflow": "ellipsis",
    "white-space": "nowrap",
  },
  inactive: {
    textDecoration: "line-through",
    color: "rgb(255, 255, 255, 0.5)",
  },
  centerContent: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  typography: {
    color: theme.palette.text.primary,
  },
}));

const SettingsMessaging = () => {
  const location = useLocation();
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const { fleetId } = useParams();

  const { activeFleet } = useContext(FleetContext);
  const { activeToastMessages, fetchToastMessages } = useContext(FleetSettingsContext);
  const [loading, setLoading] = useState(false);

  const columns = ["message", "hyperlink", "dismissable", "beginDate", "endDate"];

  const toastMessages = (activeToastMessages || [])
    .map(msg => {
      const now = DateTime.now();
      const begin = DateTime.fromISO(msg.begin);
      const end = DateTime.fromISO(msg.end);

      msg.endDate = end;
      msg.active = begin <= now && end >= now;
      msg.expired = end <= now;
      return msg;
    })
    .sort((msg1, msg2) => {
      // first goes all active messages, last — all expired messages
      if (msg1.active && !msg2.active || !msg1.expired && msg2.expired) return -1;
      if (msg2.active && !msg1.active || !msg2.expired && msg1.expired) return 1;

      // desc order for expired messages, asc — for active and future
      if (!msg1.expired && !msg2.expired) return msg2.endDate > msg1.endDate ? -1 : 1;
      if (msg1.expired && msg2.expired) return msg2.endDate < msg1.endDate ? -1 : 1;

      return 0;
    });

  const getDateTime = (dateTime) => {
    const date = DateTime.fromISO(dateTime, { zone: activeFleet.timezone });

    const dateStr = date.toLocaleString(DateTime.DATE_MED);
    const timeStr = date.toLocaleString(DateTime.TIME_24_SIMPLE);

    return `${dateStr} ${timeStr}`;
  };

  const handleFetchMessages = async () => {
    setLoading(true);
    await fetchToastMessages(fleetId);
    setLoading(false);
  };

  useEffect(() => {
    if (activeFleet) {
      handleFetchMessages();
    }
  }, [activeFleet, location]);

  return (
    <Grid item container spacing={2} className={classes.messagesWrapper}>
      {loading && (
        <LinearProgress color="secondary" data-testid="loading-progress" className={classes.loading} />
      )}
      <Grid item xs={12}>
        <Typography variant="h5">{`${t("Fleet.Settings.Messaging.Title")}`}</Typography>
        <Table className={classes.table} size="small" stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHead}>
              <TableCell className={classes.tableHeadCell}></TableCell>
              {columns.map((key) => (
                <TableCell className={classes.tableHeadCell} key={key}>
                  <Typography className={classes.typography}>
                    {t(`Fleet.Settings.Messaging.Table.${key}`)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell className={classes.tableHeadCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="messaging-table-body">
            {toastMessages.map((row) => (
              <TableRow key={row.id}>
                <TableCell className={clsx(classes.tableCell, row.expired && classes.inactive)}>
                  {row.active &&
                    <div className={classes.centerContent}>
                      <SvgIcon
                        fontSize="small"
                        htmlColor={theme.palette.statusColors.available}
                      >
                        <circle cx="12" cy="12" r="6" />
                      </SvgIcon>
                    </div>
                  }
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.expired && classes.inactive)}>
                  {row.message}
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.urlCell,
                    row.expired && classes.inactive,
                  )}
                >
                  <Link href={row.hyperlink} color="inherit" target="_blank">
                    {row.hyperlink}
                  </Link>
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.expired && classes.inactive)}>
                  {t(`Fleet.Settings.Messaging.Dismissable.${row.dismissable}`)}
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.expired && classes.inactive)}>
                  {getDateTime(row.begin)}
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.expired && classes.inactive)}>
                  {getDateTime(row.end)}
                </TableCell>
                <TableCell padding="none" className={classes.tableCell}>
                  <Button
                    component={RouterLink}
                    to={{
                      pathname: `/fleets/${fleetId}/settings/messaging/${row.id}/edit`,
                      state: { settingsBackground: location },
                    }}
                  >
                    {t("Fleet.Settings.Messaging.Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

export default SettingsMessaging;
